import React, { memo, useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, Text } from '@Sage/hoi-poi-ui';
import moment from 'moment';

import { ServerListActions } from 'actions';
import FieldWrapper from 'containers/components/Fields/FieldWrapper';
import Select from 'containers/components/Fields/Select';
import { getLiteral } from 'utils/getLiteral';

import './styles.scss';

const PeriodField = memo(({ changeField, data, field }) => {
    const dispatch = useDispatch();
    const { readOnly: fieldReadOnly } = field;

    // Get period options from Redux state
    const serverListState = useSelector((state) => state.serverList);

    // Extract period and begins fields from inputs
    const periodInput = useMemo(
        () => field.inputs?.find((input) => input.id === 'period'),
        [field.inputs],
    );

    const beginsInput = useMemo(
        () => field.inputs?.find((input) => input.id === 'begins'),
        [field.inputs],
    );

    const listName = periodInput?.inputAttrs?.list;

    // Get options from server list
    const periodOptions = useMemo(() => {
        if (!listName) return [];
        return serverListState[listName]?.data || [];
    }, [listName, serverListState]);

    // Load period options on mount
    useEffect(() => {
        if (listName) {
            dispatch(ServerListActions.getList(listName));
        }
    }, [dispatch, listName]);

    // ========================================
    // CALLBACKS
    // ========================================
    const onChangePeriod = useCallback(
        (newValue) => {
            changeField('period')(newValue?.value || null);
        },
        [changeField],
    );

    const onChangeIsPeriodCustom = useCallback(() => {
        changeField('isPeriodCustom')(!data.isPeriodCustom);
    }, [changeField, data.isPeriodCustom]);

    const onChangeBegins = useCallback(
        (idSubfield) => (value) => {
            const backendFormatDateIn = 'YYYY-MM-DD HH:mm:ss';
            const momentFormatDateIn = 'YYYY-MM-DD';
            const momentYear = moment().format('YYYY');
            const momentMonth = moment(data.begins, backendFormatDateIn).format('MM');
            const momentDay = moment(data.begins, backendFormatDateIn).format('DD');

            let date = '';
            let finalValue = value.value >= 1 && value.value <= 9 ? `0${value.value}` : value.value;

            if (data.period === 'week') {
                let momentDate = moment(data.begins, backendFormatDateIn);
                momentDate.isoWeekday(value.value);
                date = momentDate.format(backendFormatDateIn);
            } else if (data.period === 'month') {
                date = moment(
                    `${momentYear}-${momentMonth}-${finalValue}`,
                    momentFormatDateIn,
                ).format(backendFormatDateIn);
            } else if (
                data.period === 'quarter' ||
                data.period === 'semester' ||
                data.period === 'fourMonthly' ||
                data.period === 'year'
            ) {
                if (idSubfield === 'one') {
                    const momentInMonth = moment(
                        `${momentYear}-${finalValue}-01`,
                        momentFormatDateIn,
                    );
                    if (momentInMonth.daysInMonth() < parseInt(momentDay, 10)) {
                        let finalDay = momentInMonth.daysInMonth();
                        if (parseInt(finalValue, 10) === 2) {
                            // prevent possible 29th of February
                            finalDay = 28;
                        }
                        date = moment(
                            `${momentYear}-${finalValue}-${finalDay}`,
                            momentFormatDateIn,
                        ).format(backendFormatDateIn);
                    } else {
                        date = moment(
                            `${momentYear}-${finalValue}-${momentDay}`,
                            momentFormatDateIn,
                        ).format(backendFormatDateIn);
                    }
                } else if (idSubfield === 'two') {
                    date = moment(
                        `${momentYear}-${momentMonth}-${finalValue}`,
                        momentFormatDateIn,
                    ).format(backendFormatDateIn);
                }
            } else {
                date = moment(`${momentYear}-01-01`, momentFormatDateIn).format(
                    backendFormatDateIn,
                );
            }

            changeField('begins')(date);
        },
        [changeField, data.begins, data.period],
    );

    // ========================================
    // MEMOIZED VALUES
    // ========================================
    // Resolve value with label from loaded options
    const periodValue = useMemo(() => {
        const val = data.period;
        if (!val) return undefined;

        // If already an object with value and label, use it
        if (typeof val === 'object' && val.value && val.label) {
            return val;
        }

        // Get primitive value
        const primitiveValue = typeof val === 'object' ? val.value : val;
        if (!primitiveValue) return undefined;

        // Find label from loaded options
        const found = periodOptions.find((opt) => String(opt.value) === String(primitiveValue));
        if (found) {
            return { value: String(found.value), label: found.label };
        }

        return { value: String(primitiveValue), label: String(primitiveValue) };
    }, [data.period, periodOptions]);

    const beginsValueOne = useMemo(() => {
        if (!data.begins) return beginsInput?.inputAttrs?.options?.[0];
        if (data.period === 'week') {
            const idx = moment(data.begins).isoWeekday() - 1;
            return beginsInput?.inputAttrs?.options?.[idx] || beginsInput?.inputAttrs?.options?.[0];
        }
        const idx = moment(data.begins).format('M') - 1;
        return beginsInput?.inputAttrs?.options?.[idx] || beginsInput?.inputAttrs?.options?.[0];
    }, [data.begins, data.period, beginsInput?.inputAttrs?.options]);

    const beginsValueTwo = useMemo(() => {
        if (!data.begins) return beginsInput?.inputAttrs?.optionsTwo?.[0];
        const idx = moment(data.begins).format('DD') - 1;
        return (
            beginsInput?.inputAttrs?.optionsTwo?.[idx] || beginsInput?.inputAttrs?.optionsTwo?.[0]
        );
    }, [data.begins, beginsInput?.inputAttrs?.optionsTwo]);

    const showCustomizeLink = data.period !== 'day' && !data.id;
    const showBeginsFields = data.isPeriodCustom && data.period !== 'day';

    // ========================================
    // SELECT PROPS
    // ========================================
    const periodSelectProps = useMemo(
        () => ({
            isClearable: false,
            label: periodInput?.label,
            labelMode: 'horizontal',
            mandatory: periodInput?.mandatory,
            onChange: onChangePeriod,
            options: periodOptions,
            readOnly: fieldReadOnly || periodInput?.readOnly,
            value: periodValue,
        }),
        [
            fieldReadOnly,
            onChangePeriod,
            periodInput?.label,
            periodInput?.mandatory,
            periodInput?.readOnly,
            periodOptions,
            periodValue,
        ],
    );

    const beginsOneSelectProps = useMemo(
        () => ({
            isClearable: false,
            onChange: onChangeBegins('one'),
            options: beginsInput?.inputAttrs?.options || [],
            readOnly: beginsInput?.readOnly,
            value: beginsValueOne,
        }),
        [beginsInput?.inputAttrs?.options, beginsInput?.readOnly, beginsValueOne, onChangeBegins],
    );

    const beginsTwoSelectProps = useMemo(
        () => ({
            isClearable: false,
            onChange: onChangeBegins('two'),
            options: beginsInput?.inputAttrs?.optionsTwo || [],
            readOnly: beginsInput?.readOnly,
            value: beginsValueTwo,
        }),
        [
            beginsInput?.inputAttrs?.optionsTwo,
            beginsInput?.readOnly,
            beginsValueTwo,
            onChangeBegins,
        ],
    );

    if (field.hidden) return null;

    return (
        <div className="fm-period-field">
            <div className="fm-period-field__period">
                <Select {...periodSelectProps} />
            </div>

            {/* Footer with period info and customize link */}
            {data.period !== 'day' && (
                <div className="fm-period-field__footer">
                    <div className="fm-period-field__footer-left">
                        {!data.isPeriodCustom && periodInput?.inputAttrs?.footer && (
                            <Text type="caption">{periodInput.inputAttrs.footer}</Text>
                        )}
                    </div>
                    {showCustomizeLink && (
                        <div className="fm-period-field__footer-right">
                            <Link type="caption" onClick={onChangeIsPeriodCustom}>
                                {!data.isPeriodCustom
                                    ? getLiteral('action_customize_periodicity')
                                    : getLiteral('action_customize_periodicity_close')}
                            </Link>
                        </div>
                    )}
                </div>
            )}

            {/* Begins fields when customizing */}
            {showBeginsFields && (
                <>
                    <FieldWrapper
                        description={beginsInput?.description}
                        label={beginsInput?.label}
                        labelMode="horizontal"
                        mandatory
                    >
                        <div className="fm-period-field__begins-fields">
                            {beginsInput?.inputAttrs?.options?.length > 0 && (
                                <Select {...beginsOneSelectProps} />
                            )}
                            {beginsInput?.inputAttrs?.optionsTwo?.length > 0 && (
                                <Select {...beginsTwoSelectProps} />
                            )}
                        </div>
                    </FieldWrapper>
                    {beginsInput?.inputAttrs?.footer && (
                        <div className="fm-period-field__begins-footer">
                            <Text type="caption">{beginsInput.inputAttrs.footer}</Text>
                        </div>
                    )}
                </>
            )}
        </div>
    );
});

PeriodField.displayName = 'PeriodField';

export default PeriodField;
